import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";

import { ThemeProvider, withStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Drawer from "@material-ui/core/Drawer";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import FilterListIcon from "@material-ui/icons/FilterList";

import listingTheme from "./listingTheme";
import TireListingCard from "./TireListingCard";
import TireListFiltersPanel from "./TireListFiltersPanel";
import { formatIls } from "./tirePairModel";
import { findLimitedOffer, getLimitedOfferEanSet, prepareListingProducts } from "./tireListModel";
import {
  DEFAULT_LIST_FILTERS,
  LIST_PAGE_SIZE,
  applyListFilters,
  countActiveListFilters,
  getEffectiveDefaultType,
  getListFacets,
  getSearchForListFilters,
  getVisibleListFilterSections,
  parseListFiltersFromSearch,
} from "./tireListFilters";

const SORT_OPTIONS = [
  { value: "recommended", label: "מומלץ" },
  { value: "price_asc", label: "מחיר: מהנמוך לגבוה" },
  { value: "price_desc", label: "מחיר: מהגבוה לנמוך" },
  { value: "score_desc", label: "ציון: מהגבוה לנמוך" },
];

const TYPE_CHIP_LABELS = {
  all: "כולל סטים",
  single: "צמיגים בודדים",
  set: "סטים",
};

const STOCK_CHIP_LABELS = {
  local: "במלאי בארץ",
  eu: "מלאי אירופה",
};

const styles = (theme) => ({
  pageRoot: {
    width: "100%",
    maxWidth: 1200,
    boxSizing: "border-box",
    marginTop: 53,
    marginRight: "auto",
    marginBottom: theme.spacing(4),
    marginLeft: "auto",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  mobileToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    position: "sticky",
    top: 60,
    zIndex: 4,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  columns: {
    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "flex-start",
    },
  },
  sidebar: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "block",
      width: 270,
      flex: "0 0 270px",
      position: "sticky",
      top: 72,
      maxHeight: "calc(100vh - 88px)",
      overflowY: "auto",
      marginRight: theme.spacing(3),
    },
  },
  results: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  desktopToolbar: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
  },
  mobileCount: {
    display: "block",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  sortControl: {
    minWidth: 0,
    flex: "1 1 150px",
    backgroundColor: theme.palette.background.paper,
    "& .MuiOutlinedInput-input": {
      paddingTop: 10,
      paddingBottom: 10,
    },
    [theme.breakpoints.up("md")]: {
      flex: "0 0 auto",
      minWidth: 220,
    },
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  filterChip: {
    backgroundColor: theme.palette.background.paper,
  },
  resultsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  showMore: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
  showMoreButton: {
    minWidth: 220,
  },
  emptyState: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    textAlign: "center",
  },
  emptyText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  skeletonCard: {
    display: "flex",
    alignItems: "stretch",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
    marginBottom: theme.spacing(2),
    animation: "$listPulse 1.6s ease-in-out infinite",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  skeletonMedia: {
    flexShrink: 0,
    width: 200,
    minHeight: 168,
    backgroundColor: theme.palette.action.hover,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: 140,
    },
  },
  skeletonDetails: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  skeletonTitle: {
    height: 18,
    width: "52%",
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
  },
  skeletonRow: {
    height: 12,
    width: "78%",
    borderRadius: 6,
    marginTop: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
  },
  skeletonPrice: {
    flexShrink: 0,
    boxSizing: "border-box",
    width: 220,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      borderLeft: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  skeletonPriceLine: {
    height: 22,
    width: "70%",
    alignSelf: "center",
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
  },
  skeletonButton: {
    height: 36,
    marginTop: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
  },
  "@keyframes listPulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.55 },
    "100%": { opacity: 1 },
  },
  "@media (prefers-reduced-motion: reduce)": {
    skeletonCard: {
      animation: "none",
    },
  },
  drawerPaper: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  drawerHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    marginTop: theme.spacing(1),
    marginRight: "auto",
    marginLeft: "auto",
    backgroundColor: theme.palette.divider,
  },
  drawerTitle: {
    paddingTop: theme.spacing(1.5),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  drawerBody: {
    overflowY: "auto",
    padding: theme.spacing(2),
    flex: "1 1 auto",
  },
  drawerFooter: {
    position: "sticky",
    bottom: 0,
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  "@media (min-width: 1024px)": {
    pageRoot: {
      margin: "130px auto",
    },
    sidebar: {
      top: 146,
      maxHeight: "calc(100vh - 162px)",
    },
  },
});

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getSliderValue(filters, facets, priceDraft) {
  if (
    facets.priceMin == null ||
    facets.priceMax == null ||
    facets.priceMin === facets.priceMax
  ) {
    return null;
  }

  if (Array.isArray(priceDraft) && priceDraft.length === 2) {
    return priceDraft;
  }

  const low = filters.minPrice == null ? facets.priceMin : filters.minPrice;
  const high = filters.maxPrice == null ? facets.priceMax : filters.maxPrice;
  return [
    clamp(Math.min(low, high), facets.priceMin, facets.priceMax),
    clamp(Math.max(low, high), facets.priceMin, facets.priceMax),
  ];
}

function brandLabel(key, facets) {
  const match = (facets.brands || []).find((item) => item.key === key);
  return match ? match.label : key;
}

function priceChipLabel(filters) {
  const { minPrice, maxPrice } = filters;
  if (minPrice != null && maxPrice != null) {
    return `${formatIls(minPrice)} עד ${formatIls(maxPrice)}`;
  }
  if (minPrice != null) {
    return `מ-${formatIls(minPrice)}`;
  }
  return `עד ${formatIls(maxPrice)}`;
}

class TireListingView extends React.Component {
  state = {
    drawerOpen: false,
    priceDraft: null,
    visibleCount: LIST_PAGE_SIZE,
  };

  componentDidUpdate(prevProps) {
    const prevSearch = (prevProps.location && prevProps.location.search) || "";
    const nextSearch = (this.props.location && this.props.location.search) || "";
    const searchChanged = prevSearch !== nextSearch;
    const keyChanged = prevProps.resetKey !== this.props.resetKey;
    const nextState = {};

    if ((searchChanged || keyChanged) && this.state.visibleCount !== LIST_PAGE_SIZE) {
      nextState.visibleCount = LIST_PAGE_SIZE;
    }

    if (keyChanged) {
      if (this.state.drawerOpen) {
        nextState.drawerOpen = false;
      }
      if (this.state.priceDraft) {
        nextState.priceDraft = null;
      }
    } else if (searchChanged && this.state.priceDraft) {
      nextState.priceDraft = null;
    }

    if (Object.keys(nextState).length > 0) {
      this.setState(nextState);
    }
  }

  getPrepared() {
    const { products, limitedTimeOffers } = this.props;
    if (this._memo && this._memo.products === products && this._memo.offers === limitedTimeOffers) {
      return this._memo;
    }

    const prepared = prepareListingProducts(products || []);
    this._memo = {
      products,
      offers: limitedTimeOffers,
      prepared,
      facets: getListFacets(prepared),
      limitedOfferEanSet: getLimitedOfferEanSet(limitedTimeOffers || []),
    };
    return this._memo;
  }

  searchString() {
    return (this.props.location && this.props.location.search) || "";
  }

  filterOptions() {
    return {
      defaultType: getEffectiveDefaultType(this.props.defaultType, this.getPrepared().facets),
    };
  }

  writeFilters = (nextFilters) => {
    const { history, location } = this.props;
    if (!history || !location) {
      return;
    }

    const search = getSearchForListFilters(location.search || "", nextFilters, this.filterOptions());
    if (search === (location.search || "")) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search,
      hash: location.hash,
    });
  };

  updateFilters = (patch) => {
    const current = parseListFiltersFromSearch(this.searchString(), this.filterOptions());
    this.writeFilters({ ...current, ...patch });
  };

  clearFilters = () => {
    const options = this.filterOptions();
    const current = parseListFiltersFromSearch(this.searchString(), options);
    this.writeFilters({
      ...DEFAULT_LIST_FILTERS,
      type: options.defaultType,
      brands: [],
      stock: [],
      rims: [],
      sort: current.sort,
    });
  };

  handleSortChange = (event) => {
    this.updateFilters({ sort: event.target.value });
  };

  handlePriceChange = (value) => {
    this.setState({ priceDraft: Array.isArray(value) ? value.slice() : value });
  };

  handlePriceCommitted = (value) => {
    if (!Array.isArray(value) || value.length < 2) {
      return;
    }

    const facets = this.getPrepared().facets;
    let min = Math.round(Number(value[0]));
    let max = Math.round(Number(value[1]));
    if (min > max) {
      const swap = min;
      min = max;
      max = swap;
    }

    this.updateFilters({
      minPrice: facets.priceMin == null || min <= facets.priceMin ? null : min,
      maxPrice: facets.priceMax == null || max >= facets.priceMax ? null : max,
    });
  };

  showMore = () => {
    this.setState((state) => ({ visibleCount: state.visibleCount + LIST_PAGE_SIZE }));
  };

  openDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  closeDrawer = () => {
    this.setState({ drawerOpen: false });
  };

  renderSortControl(filters) {
    const { classes } = this.props;

    return (
      <FormControl variant="outlined" className={classes.sortControl}>
        <Select
          value={filters.sort}
          onChange={this.handleSortChange}
          inputProps={{ "aria-label": "מיון" }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  buildViewModel() {
    const preparedState = this.getPrepared();
    const options = this.filterOptions();
    const filters = parseListFiltersFromSearch(this.searchString(), options);
    const visible = applyListFilters(preparedState.prepared, filters, {
      limitedOfferEanSet: preparedState.limitedOfferEanSet,
    });

    return {
      filters,
      facets: preparedState.facets,
      prepared: preparedState.prepared,
      visible,
      shown: visible.slice(0, this.state.visibleCount),
      activeCount: countActiveListFilters(filters, options),
      defaultType: options.defaultType,
      typeTotal: applyListFilters(preparedState.prepared, { type: filters.type }, {
        limitedOfferEanSet: preparedState.limitedOfferEanSet,
      }).length,
      sliderValue: getSliderValue(filters, preparedState.facets, this.state.priceDraft),
      sections: getVisibleListFilterSections(preparedState.facets),
    };
  }

  renderChips(filters, facets, defaultType) {
    const { classes } = this.props;
    const chips = [];

    filters.brands.forEach((brand) => {
      chips.push({
        key: `brand-${brand}`,
        label: brandLabel(brand, facets),
        onDelete: () => this.updateFilters({
          brands: filters.brands.filter((item) => item !== brand),
        }),
      });
    });

    filters.stock.forEach((stock) => {
      chips.push({
        key: `stock-${stock}`,
        label: STOCK_CHIP_LABELS[stock] || stock,
        onDelete: () => this.updateFilters({
          stock: filters.stock.filter((item) => item !== stock),
        }),
      });
    });

    if (filters.freeShippingOnly) {
      chips.push({
        key: "ship",
        label: "משלוח חינם",
        onDelete: () => this.updateFilters({ freeShippingOnly: false }),
      });
    }

    if (filters.type !== defaultType) {
      chips.push({
        key: "type",
        label: TYPE_CHIP_LABELS[filters.type],
        onDelete: () => this.updateFilters({ type: defaultType }),
      });
    }

    filters.rims.forEach((rim) => {
      chips.push({
        key: `rim-${rim}`,
        label: (
          <React.Fragment>
            {"קוטר "}
            <bdi dir="ltr">{`${rim}"`}</bdi>
          </React.Fragment>
        ),
        onDelete: () => this.updateFilters({
          rims: filters.rims.filter((item) => item !== rim),
        }),
      });
    });

    if (filters.minPrice != null || filters.maxPrice != null) {
      chips.push({
        key: "price",
        label: priceChipLabel(filters),
        onDelete: () => this.updateFilters({ minPrice: null, maxPrice: null }),
      });
    }

    if (chips.length === 0) {
      return null;
    }

    return (
      <div className={classes.chips}>
        {chips.map((chip) => (
          <Chip
            key={chip.key}
            size="small"
            variant="outlined"
            label={chip.label}
            onDelete={chip.onDelete}
            className={classes.filterChip}
          />
        ))}
        <Button size="small" color="primary" onClick={this.clearFilters}>
          נקה הכל
        </Button>
      </div>
    );
  }

  renderSkeletons() {
    const { classes } = this.props;

    return (
      <div aria-hidden="true">
        {[0, 1, 2, 3].map((index) => (
          <div className={classes.skeletonCard} key={index}>
            <div className={classes.skeletonMedia} />
            <div className={classes.skeletonDetails}>
              <div className={classes.skeletonTitle} />
              <div className={classes.skeletonRow} />
              <div className={classes.skeletonRow} />
            </div>
            <div className={classes.skeletonPrice}>
              <div className={classes.skeletonPriceLine} />
              <div className={classes.skeletonButton} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderResults(model) {
    const { classes, limitedTimeOffers, availabilityContext, addProductsToCart, collectMetric, metricSource } = this.props;
    const { filters, facets, visible, shown, sliderValue, sections, typeTotal } = model;
    const countLabel = visible.length === typeTotal
      ? `${typeTotal} תוצאות`
      : `מציג ${visible.length} מתוך ${typeTotal} תוצאות`;
    const remaining = visible.length - shown.length;
    const panelProps = {
      filters,
      facets,
      sliderValue,
      onChange: this.updateFilters,
      onPriceChange: this.handlePriceChange,
      onPriceCommitted: this.handlePriceCommitted,
    };

    return (
      <div className={classes.columns}>
        {sections.any && (
          <aside className={classes.sidebar}>
            <TireListFiltersPanel {...panelProps} />
          </aside>
        )}
        <div className={classes.results}>
          <div className={classes.desktopToolbar}>
            <Typography variant="body2" color="textSecondary" aria-live="polite">
              {countLabel}
            </Typography>
            {this.renderSortControl(filters)}
          </div>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.mobileCount}
            aria-live="polite"
          >
            {countLabel}
          </Typography>
          {this.renderChips(filters, facets, model.defaultType)}
          {visible.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="h6" component="h2">
                אין תוצאות לסינון הזה
              </Typography>
              <Typography variant="body2" color="textSecondary" className={classes.emptyText}>
                נסו להרחיב את החיפוש או לנקות את הסינון
              </Typography>
              <Button variant="outlined" color="primary" onClick={this.clearFilters}>
                נקה סינון
              </Button>
            </div>
          ) : (
            <div className={classes.resultsList}>
              {shown.map((product) => (
                <TireListingCard
                  key={product.ean}
                  product={product}
                  limitedOffer={findLimitedOffer(product, limitedTimeOffers)}
                  availabilityContext={availabilityContext}
                  addProductsToCart={addProductsToCart}
                  collectMetric={collectMetric}
                  metricSource={metricSource}
                />
              ))}
            </div>
          )}
          {remaining > 0 ? (
            <div className={classes.showMore}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {`מציג ${shown.length} מתוך ${visible.length}`}
              </Typography>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                className={classes.showMoreButton}
                onClick={this.showMore}
              >
                {`הצג עוד ${Math.min(LIST_PAGE_SIZE, remaining)}`}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  render() {
    const { classes, header, pending, emptyState } = this.props;
    const { drawerOpen } = this.state;
    const model = pending ? null : this.buildViewModel();
    const prepared = model ? model.prepared : [];
    const hasResults = prepared.length > 0;

    return (
      <React.Fragment>
        <div className={classes.pageRoot} dir="rtl">
          {header}

          {pending && this.renderSkeletons()}

          {!pending && !hasResults && emptyState}

          {!pending && hasResults && (
            <div className={classes.mobileToolbar}>
              {model.sections.any && (
                <Badge
                  color="primary"
                  overlap="rectangular"
                  badgeContent={model.activeCount}
                  invisible={model.activeCount === 0}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={this.openDrawer}
                    startIcon={<FilterListIcon />}
                  >
                    סינון
                  </Button>
                </Badge>
              )}
              {this.renderSortControl(model.filters)}
            </div>
          )}

          {!pending && hasResults && this.renderResults(model)}
        </div>

        {model ? (
          <Drawer
            anchor="bottom"
            open={drawerOpen && hasResults && model.sections.any}
            onClose={this.closeDrawer}
            classes={{ paper: classes.drawerPaper }}
          >
            <div className={classes.drawerHandle} />
            <Typography variant="h6" component="h2" className={classes.drawerTitle}>
              סינון
            </Typography>
            <div className={classes.drawerBody}>
              <TireListFiltersPanel
                variant="plain"
                filters={model.filters}
                facets={model.facets}
                sliderValue={model.sliderValue}
                onChange={this.updateFilters}
                onPriceChange={this.handlePriceChange}
                onPriceCommitted={this.handlePriceCommitted}
              />
            </div>
            <div className={classes.drawerFooter}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={this.closeDrawer}
              >
                {`הצג ${model.visible.length} תוצאות`}
              </Button>
            </div>
          </Drawer>
        ) : null}
      </React.Fragment>
    );
  }
}

TireListingView.propTypes = {
  classes: PropTypes.object.isRequired,
  header: PropTypes.node,
  products: PropTypes.array,
  pending: PropTypes.bool,
  emptyState: PropTypes.node,
  availabilityContext: PropTypes.object,
  limitedTimeOffers: PropTypes.array,
  addProductsToCart: PropTypes.func.isRequired,
  collectMetric: PropTypes.func.isRequired,
  metricSource: PropTypes.string.isRequired,
  resetKey: PropTypes.string,
  defaultType: PropTypes.oneOf(["all", "single", "set"]),
  location: PropTypes.object,
  history: PropTypes.object,
};

TireListingView.defaultProps = {
  header: null,
  products: [],
  pending: false,
  emptyState: null,
  availabilityContext: {},
  limitedTimeOffers: [],
  resetKey: "",
  defaultType: "all",
};

const StyledTireListingView = withStyles(styles)(TireListingView);

function ThemedTireListingView(props) {
  return (
    <ThemeProvider theme={listingTheme}>
      <StyledTireListingView {...props} />
    </ThemeProvider>
  );
}

export default withRouter(ThemedTireListingView);
